import { apiRequest, apiRoutes } from '../../api';
import { useState, useCallback, useRef, useEffect } from 'react';
import { Search as SearchIcon, LocationOn as LocationIcon, Home as HomeIcon, LocationCity as CityIcon, Map as RegionIcon } from '@mui/icons-material';
import './Search.css';

function Search({ 
    mapRef, 
    setViewState,
    showSearch,
    setShowSearch,
    setShowLayerControl,
    onGeoFilterChange,
    activeGeoFilters,
}) {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchType, setSearchType] = useState('location'); // 'location' | 'city' | 'region' | 'neighborhood'
    const searchInputRef = useRef(null);

    // Close other panels when search is opened
    useEffect(() => {
        if (showSearch) {
            setShowLayerControl(false);
        }
    }, [showSearch, setShowLayerControl]);

    // Focus input when search panel opens
    useEffect(() => {
        if (showSearch && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [showSearch]);

    // Search for locations using Nominatim
    const searchLocations = async (query) => {
        if (!query.trim()) return [];

        try {
            const response = await apiRequest(
                `https://nominatim.openstreetmap.org/search?` +
                `q=${encodeURIComponent(query)}&` +
                `format=json&` +
                `limit=10&` +
                `countrycodes=ir&` +
                `addressdetails=1&` +
                `accept-language=fa`
            );

            const data = await response.json();
            return data.map(item => ({
                id: item.place_id,
                name: item.display_name,
                lat: parseFloat(item.lat),
                lon: parseFloat(item.lon),
                type: 'location',
                category: item.type,
                importance: item.importance || 0
            }));
        } catch (error) {

            return [];
        }
    };

    // Search for neighborhoods using Django backend API
    const searchNeighborhoods = async (query) => {
        if (!query.trim()) return [];

        try {
            const response = await apiRequest(
                apiRoutes.locations.neighborhoodSearch(query)
            );

            if (!response.ok) {

                return [];
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {

                return [];
            }

            const data = await response.json();

            if (data.results) {
                return data.results.map(neighborhood => ({
                    id: neighborhood.id,
                    name: neighborhood.name,
                    lat: neighborhood.centroid ? neighborhood.centroid.lat : null,
                    lon: neighborhood.centroid ? neighborhood.centroid.lon : null,
                    type: 'neighborhood',
                    city: neighborhood.city,
                    region: neighborhood.region
                })); // include all — flyTo is skipped if no centroid
            }

            return [];
        } catch (error) {

            return [];
        }
    };

    // Search cities via backend API
    const searchCities = async (query) => {
        if (!query.trim()) return [];
        try {
            const response = await apiRequest(apiRoutes.locations.citySearch(query));
            if (!response.ok) return [];
            const data = await response.json();
            return (data.results || []).map(city => ({
                id: city.id,
                name: city.name,
                lat: city.centroid ? city.centroid.lat : null,
                lon: city.centroid ? city.centroid.lon : null,
                bbox: city.bbox || null,
                type: 'city',
                subtitle: city.province || '',
            }));
        } catch (error) {

            return [];
        }
    };

    // Search regions via backend API
    const searchRegions = async (query) => {
        if (!query.trim()) return [];
        try {
            const response = await apiRequest(apiRoutes.locations.regionSearch(query));
            if (!response.ok) return [];
            const data = await response.json();
            return (data.results || []).map(region => ({
                id: region.id,
                name: region.name,
                lat: region.centroid ? region.centroid.lat : null,
                lon: region.centroid ? region.centroid.lon : null,
                bbox: region.bbox || null,
                type: 'region',
                subtitle: region.city || '',
            }));
        } catch (error) {

            return [];
        }
    };

    // Handle search input change with debouncing
    const handleSearchChange = useCallback(async (value) => {
        setSearchQuery(value);

        if (!value.trim()) {
            setSearchResults([]);
            return;
        }

        setIsLoading(true);

        try {
            let results = [];

            if (searchType === 'location') {
                results = await searchLocations(value);
            } else if (searchType === 'neighborhood') {
                results = await searchNeighborhoods(value);
            } else if (searchType === 'city') {
                results = await searchCities(value);
            } else if (searchType === 'region') {
                results = await searchRegions(value);
            }

            setSearchResults(results);
        } catch (error) {

            setSearchResults([]);
        } finally {
            setIsLoading(false);
        }
    }, [searchType]);

    // Debounced search
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            handleSearchChange(searchQuery);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchQuery, handleSearchChange]);

    // Handle result selection
    const handleResultSelect = useCallback((result) => {
        const isGeoType = result.type === 'city' || result.type === 'region' || result.type === 'neighborhood';

        // Toggle geo filter — fly map but keep panel open for multi-select
        if (isGeoType && onGeoFilterChange) {
            onGeoFilterChange({ type: result.type, id: result.id, name: result.name });
        }

        // Fly map only if coordinates exist
        if (setViewState && result.lat && result.lon) {
            if ((result.type === 'city' || result.type === 'region') && result.bbox) {
                const centerLat = (result.bbox.min_lat + result.bbox.max_lat) / 2;
                const centerLon = (result.bbox.min_lon + result.bbox.max_lon) / 2;
                const zoom = result.type === 'city' ? 11 : 13;
                setViewState(prev => ({ ...prev, longitude: centerLon, latitude: centerLat, zoom, transitionDuration: 1500 }));
            } else if (result.type === 'neighborhood') {
                setViewState(prev => ({ ...prev, longitude: result.lon, latitude: result.lat, zoom: 14, transitionDuration: 1500 }));
            } else {
                setViewState(prev => ({ ...prev, longitude: result.lon, latitude: result.lat, zoom: Math.max(prev.zoom, 15), transitionDuration: 1500 }));
            }
        }

        // Only close panel for plain location results
        if (!isGeoType) {
            setSearchQuery('');
            setSearchResults([]);
            setShowSearch(false);
        }
    }, [setViewState, setShowSearch, onGeoFilterChange]);

    // Clear search
    const clearSearch = useCallback(() => {
        setSearchQuery('');
        setSearchResults([]);
    }, []);

    return (
        <>
            {/* Search Toggle Button */}
            <button
                onClick={() => setShowSearch(!showSearch)}
                className={`search-toggle-button ${showSearch ? 'active' : ''}`}
                title="جستجو"
            >
                <SearchIcon />
            </button>

            {/* Search Panel */}
            {showSearch && (
                <div className="search-panel">
                    <div className="search-panel-title">
                        جستجو در نقشه
                    </div>

                    {/* Search Type Toggle */}
                    <div className="search-type-toggle">
                        <button
                            onClick={() => setSearchType('location')}
                            className={`search-type-button ${searchType === 'location' ? 'active' : 'inactive'}`}
                        >
                            <LocationIcon style={{ fontSize: '14px' }} />
                            جستجوی مکان
                        </button>
                        <button
                            onClick={() => setSearchType('city')}
                            className={`search-type-button ${searchType === 'city' ? 'active' : 'inactive'}`}
                        >
                            <CityIcon style={{ fontSize: '14px' }} />
                            جستجو شهر
                        </button>
                        <button
                            onClick={() => setSearchType('region')}
                            className={`search-type-button ${searchType === 'region' ? 'active' : 'inactive'}`}
                        >
                            <RegionIcon style={{ fontSize: '14px' }} />
                            جستجو منطقه
                        </button>
                        <button
                            onClick={() => setSearchType('neighborhood')}
                            className={`search-type-button ${searchType === 'neighborhood' ? 'active' : 'inactive'}`}
                        >
                            <HomeIcon style={{ fontSize: '14px' }} />
                            جستجوی محله
                        </button>
                    </div>

                    {/* Selected geo filters strip */}
                    {(activeGeoFilters || []).length > 0 && (
                        <div className="search-selected-strip">
                            {activeGeoFilters.map(f => (
                                <span key={`${f.type}_${f.id}`} className="search-selected-chip">
                                    {f.name}
                                    <button
                                        className="search-selected-remove"
                                        onClick={() => onGeoFilterChange(f)}
                                        aria-label={`حذف ${f.name}`}
                                    >×</button>
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Search Input */}
                    <div className="search-input-container">
                        <input
                            ref={searchInputRef}
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={
                                searchType === 'location' ? 'نام مکان را وارد کنید...' :
                                searchType === 'city' ? 'نام شهر را وارد کنید...' :
                                searchType === 'region' ? 'نام منطقه را وارد کنید...' :
                                'نام محله را وارد کنید...'
                            }
                            className="search-input"
                        />
                        {searchQuery && (
                            <button
                                onClick={clearSearch}
                                className="search-clear-button"
                            >
                                ×
                            </button>
                        )}
                    </div>

                    {/* Loading Indicator */}
                    {isLoading && (
                        <div className="search-loading">
                            در حال جستجو...
                        </div>
                    )}

                    {/* Search Results */}
                    {searchResults.length > 0 && !isLoading && (
                        <div className="search-results">
                            {searchResults.map((result) => {
                                const isSelected = (activeGeoFilters || []).some(
                                    f => f.type === result.type && f.id === result.id
                                );
                                return (
                                    <div
                                        key={result.id}
                                        onClick={() => handleResultSelect(result)}
                                        className={`search-result-item ${isSelected ? 'selected' : ''}`}
                                    >
                                        <div className={`search-result-icon ${result.type}`}>
                                            {result.type === 'neighborhood' ? <HomeIcon style={{ fontSize: '16px' }} /> :
                                             result.type === 'city' ? <CityIcon style={{ fontSize: '16px' }} /> :
                                             result.type === 'region' ? <RegionIcon style={{ fontSize: '16px' }} /> :
                                             <LocationIcon style={{ fontSize: '16px' }} />}
                                        </div>
                                        <div className="search-result-content">
                                            <div className="search-result-name">{result.name}</div>
                                            <div className="search-result-category">
                                                {result.type === 'neighborhood' ? `محله${result.region ? ` — ${result.region}` : ''}` :
                                                 result.type === 'city' ? `شهر${result.subtitle ? ` — ${result.subtitle}` : ''}` :
                                                 result.type === 'region' ? `منطقه${result.subtitle ? ` — ${result.subtitle}` : ''}` :
                                                 result.category || 'مکان'}
                                            </div>
                                        </div>
                                        {isSelected && <span className="search-result-check">✓</span>}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* No Results */}
                    {searchQuery && searchResults.length === 0 && !isLoading && (
                        <div className="search-no-results">
                            نتیجه‌ای یافت نشد
                        </div>
                    )}

                    {/* Search Tips */}
                    {!searchQuery && (
                        <div className="search-tips">
                            <div className="search-tips-text">
                                {searchType === 'location' 
                                    ? 'نام شهر، خیابان، یا مکان مورد نظر را وارد کنید'
                                    : searchType === 'city'
                                    ? 'نام شهر مورد نظر را وارد کنید'
                                    : searchType === 'region'
                                    ? 'نام منطقه مورد نظر را وارد کنید'
                                    : 'نام محله مورد نظر را وارد کنید'
                                }
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}

export default Search;
